/**
 * Heidi Loves Pets — Shopify OS 2.0 Theme
 * predictive-search.js — Predictive search custom element
 * Uses /search/suggest.json API. Vanilla JS only. No jQuery.
 */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input       = this.querySelector('input[name="q"]');
    this.resultsEl   = this.querySelector('.predictive-search__results');
    this.form        = this.querySelector('form');

    if (!this.input || !this.resultsEl) return;

    this._debounce   = null;
    this._lastQuery  = '';
    this._isOpen     = false;

    // Bind handlers
    this._onInput          = this._onInput.bind(this);
    this._onKeyDown        = this._onKeyDown.bind(this);
    this._onDocClick       = this._onDocClick.bind(this);
    this._onInputFocus     = this._onInputFocus.bind(this);

    this.input.addEventListener('input',   this._onInput);
    this.input.addEventListener('keydown', this._onKeyDown);
    this.input.addEventListener('focus',   this._onInputFocus);
    document.addEventListener('click',     this._onDocClick);

    // Initial hidden state
    this.resultsEl.hidden = true;
    this.resultsEl.setAttribute('role', 'listbox');
    this.resultsEl.id = 'predictive-search-results';
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', 'predictive-search-results');
    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('spellcheck', 'false');
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener('input',   this._onInput);
      this.input.removeEventListener('keydown', this._onKeyDown);
      this.input.removeEventListener('focus',   this._onInputFocus);
    }
    document.removeEventListener('click', this._onDocClick);
    clearTimeout(this._debounce);
  }

  /* ---------------------------------------------------------------------------
     Event Handlers
     --------------------------------------------------------------------------- */
  _onInputFocus() {
    const query = this.input.value.trim();
    if (query.length >= 2) {
      this.fetchResults(query);
    }
  }

  _onInput(event) {
    const query = event.target.value.trim();

    if (query === this._lastQuery) return;
    this._lastQuery = query;

    if (query.length < 2) {
      this._closeResults();
      return;
    }

    clearTimeout(this._debounce);
    this._debounce = setTimeout(() => this.fetchResults(query), 300);
  }

  _onKeyDown(e) {
    if (!this._isOpen) return;

    const items = Array.from(this.resultsEl.querySelectorAll('.predictive-search__item[data-url]'));

    if (e.key === 'Escape') {
      this._closeResults();
      this.input.focus();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const focused = this.resultsEl.querySelector('[aria-selected="true"]');
      const nextIdx = focused ? items.indexOf(focused) + 1 : 0;
      this._setFocusedResult(items, nextIdx);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const focused  = this.resultsEl.querySelector('[aria-selected="true"]');
      const prevIdx  = focused ? items.indexOf(focused) - 1 : items.length - 1;
      this._setFocusedResult(items, prevIdx);
      return;
    }

    if (e.key === 'Enter') {
      const focused = this.resultsEl.querySelector('[aria-selected="true"]');
      if (focused && focused.dataset.url) {
        e.preventDefault();
        window.location.href = focused.dataset.url;
      }
    }
  }

  _setFocusedResult(items, idx) {
    // Clamp index
    const safeIdx = (idx + items.length) % items.length;
    items.forEach((item, i) => {
      const isActive = i === safeIdx;
      item.setAttribute('aria-selected', String(isActive));
      item.tabIndex = isActive ? 0 : -1;
      if (isActive) item.focus();
    });
  }

  _onDocClick(e) {
    if (this._isOpen && !this.contains(e.target)) {
      this._closeResults();
    }
  }

  /* ---------------------------------------------------------------------------
     Fetch Results
     --------------------------------------------------------------------------- */
  async fetchResults(query) {
    // Show loading state
    this._showLoading();

    const params = new URLSearchParams({
      q: query,
      'resources[type]':  'product,collection,page',
      'resources[limit]': '6',
    });

    const url = `/search/suggest.json?${params.toString()}`;

    try {
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error('Search failed: ' + res.status);

      const data    = await res.json();
      const results = (data.resources && data.resources.results) || {};
      this.renderResults(results, query);
    } catch (err) {
      console.error('[PredictiveSearch] fetchResults error:', err);
      this._renderFetchError();
    }
  }

  /* ---------------------------------------------------------------------------
     Render Results
     --------------------------------------------------------------------------- */
  renderResults(results, query) {
    const products    = results.products    || [];
    const collections = results.collections || [];
    const pages       = results.pages       || [];

    if (!products.length && !collections.length && !pages.length) {
      this.resultsEl.innerHTML = `
        <p class="predictive-search__no-results" role="status" aria-live="polite">
          No results found for "<strong>${this._escapeHtml(query)}</strong>"
        </p>
      `;
      this.resultsEl.hidden = false;
      this._isOpen          = true;
      return;
    }

    let html = '';

    /* Products */
    if (products.length) {
      html += `<div class="predictive-search__section-title">Products</div>`;
      html += products.map(product => {
        const imgSrc = product.featured_image
          ? product.featured_image.url + '&width=96'
          : null;

        const imgHTML = imgSrc
          ? `<img class="predictive-search__item-img" src="${this._escapeHtml(imgSrc)}" alt="${this._escapeHtml(product.title)}" loading="lazy" width="48" height="48">`
          : `<div class="predictive-search__item-img" aria-hidden="true"></div>`;

        const price = product.price
          ? this._formatMoney(product.price)
          : '';

        return `
          <a class="predictive-search__item"
             href="${this._escapeHtml(product.url)}"
             data-url="${this._escapeHtml(product.url)}"
             role="option"
             aria-selected="false"
             tabindex="-1">
            ${imgHTML}
            <div class="predictive-search__item-body">
              <div class="predictive-search__item-title">${this._highlightQuery(this._escapeHtml(product.title), query)}</div>
              ${price ? `<div class="predictive-search__item-price">${price}</div>` : ''}
            </div>
          </a>
        `.trim();
      }).join('');
    }

    /* Collections */
    if (collections.length) {
      html += `<div class="predictive-search__section-title">Collections</div>`;
      html += collections.map(collection => `
        <a class="predictive-search__item"
           href="${this._escapeHtml(collection.url)}"
           data-url="${this._escapeHtml(collection.url)}"
           role="option"
           aria-selected="false"
           tabindex="-1">
          <div class="predictive-search__item-body">
            <div class="predictive-search__item-title">${this._highlightQuery(this._escapeHtml(collection.title), query)}</div>
          </div>
        </a>
      `.trim()).join('');
    }

    /* Pages */
    if (pages.length) {
      html += `<div class="predictive-search__section-title">Pages</div>`;
      html += pages.map(page => `
        <a class="predictive-search__item"
           href="${this._escapeHtml(page.url)}"
           data-url="${this._escapeHtml(page.url)}"
           role="option"
           aria-selected="false"
           tabindex="-1">
          <div class="predictive-search__item-body">
            <div class="predictive-search__item-title">${this._highlightQuery(this._escapeHtml(page.title), query)}</div>
          </div>
        </a>
      `.trim()).join('');
    }

    // View all link
    html += `
      <a class="predictive-search__item predictive-search__view-all"
         href="/search?q=${encodeURIComponent(query)}"
         data-url="/search?q=${encodeURIComponent(query)}"
         role="option"
         aria-selected="false"
         tabindex="-1">
        <div class="predictive-search__item-body">
          <div class="predictive-search__item-title">View all results for "<strong>${this._escapeHtml(query)}</strong>"</div>
        </div>
      </a>
    `.trim();

    this.resultsEl.innerHTML  = html;
    this.resultsEl.hidden     = false;
    this._isOpen              = true;
    this.input.setAttribute('aria-expanded', 'true');
  }

  /* ---------------------------------------------------------------------------
     Loading / Error states
     --------------------------------------------------------------------------- */
  _showLoading() {
    this.resultsEl.innerHTML = `
      <div class="predictive-search__no-results" aria-live="polite">
        <span class="spinner" role="status"><span class="sr-only">Searching…</span></span>
      </div>
    `;
    this.resultsEl.hidden = false;
    this._isOpen          = true;
  }

  _renderFetchError() {
    this.resultsEl.innerHTML = `
      <p class="predictive-search__no-results" role="alert">
        Something went wrong. Please try again.
      </p>
    `;
    this.resultsEl.hidden = false;
    this._isOpen          = true;
  }

  _closeResults() {
    this.resultsEl.hidden = true;
    this._isOpen          = false;
    this.input.setAttribute('aria-expanded', 'false');
    clearTimeout(this._debounce);
  }

  /* ---------------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------------- */
  _highlightQuery(escapedText, query) {
    if (!query) return escapedText;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return escapedText.replace(
      new RegExp('(' + escaped + ')', 'gi'),
      '<mark>$1</mark>'
    );
  }

  _formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    const amount   = (cents / 100).toFixed(2);
    const currency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active)
      ? Shopify.currency.active
      : 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  }

  _escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#039;');
  }
}

customElements.define('predictive-search', PredictiveSearch);
